import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, ViewChild, ViewRef } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { UserAvatarComponent } from '@ui-components/user-avatar/user-avatar.component';
import { ConnectAudioService } from '@services/connect-audio.service';
import { AudioCallService } from '@services/audio-call.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { CallSessionService } from '@call/services/call-session.service';
import { ActiveAudioCallNativeService } from '@call/services/active-audio-call-native.service';
import { ActiveAudioCallUiService } from '@call/services/active-audio-call-ui.service';

interface VideoElementFrameMetrics extends HTMLVideoElement {
  webkitDecodedFrameCount?: number;
  mozPaintedFrames?: number;
}

@Component({
  selector: 'active-audio-call-widget',
  templateUrl: './active-audio-call-widget.component.html',
  styleUrls: ['./active-audio-call-widget.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    NgIf,
    AsyncPipe,
    TranslateModule,
    CdkDrag,
    UserAvatarComponent,
  ],
})
export class ActiveAudioCallWidgetComponent implements AfterViewInit, OnDestroy {
  protected readonly activeAudioCallNativeService: ActiveAudioCallNativeService = inject(ActiveAudioCallNativeService);
  protected readonly activeAudioCallUiService: ActiveAudioCallUiService = inject(ActiveAudioCallUiService);
  protected readonly connectAudioService: ConnectAudioService = inject(ConnectAudioService);
  protected readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly audioCallService: AudioCallService = inject(AudioCallService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('audioOutput', {static: false})
  protected set audioOutputRef(ref: ElementRef<HTMLAudioElement> | undefined) {
    this.audioOutputElement = ref?.nativeElement ?? null;
    this.attachPlaybackElement();
  }

  @ViewChild('remoteVideo', {static: false})
  protected set remoteVideoRef(ref: ElementRef<HTMLVideoElement> | undefined) {
    if (this.remoteVideoElement && this.remoteVideoElement !== ref?.nativeElement) {
      this.remoteVideoElement.srcObject = null;
    }

    this.remoteVideoElement = ref?.nativeElement ?? null;
    this.syncRemoteVideoElement();
    this.syncRemoteVideoPresence();
  }

  protected hasRemoteVideo: boolean = false;

  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private audioOutputElement: HTMLAudioElement | null = null;
  private remoteVideoElement: HTMLVideoElement | null = null;
  private remoteStream: MediaStream | null = null;
  private suppressNextPointerOpen: boolean = false;
  private remoteVideoTrackActive: boolean = false;
  private remoteVideoProbeVersion: number = 0;
  private remoteVideoFrameStallTimeout: ReturnType<typeof setTimeout> | null = null;
  private remoteVideoFramePollTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly remoteVideoFrameStallMs: number = 2500;
  private readonly remoteVideoFramePollIntervalMs: number = 500;

  protected get shouldShow(): boolean {
    return this.activeAudioCallUiService.isMinimized && this.hasActiveCall;
  }

  protected get partnerFullName(): string {
    const name: string | null | undefined = this.callSessionService.selectedOrder?.partnerName;
    const surname: string | null | undefined = this.callSessionService.selectedOrder?.partnerSurname;
    return [name, surname].filter(Boolean).join(' ');
  }

  protected get partnerAvatarUrl(): string | null | undefined {
    return this.callSessionService.selectedOrder?.partnerAvatar;
  }

  protected get callStatusKey(): string {
    if (!this.connectAudioService.webrtcAudioService.isConnected && this.connectAudioService.webrtcAudioService.streamStartTime > 0) {
      return 'call.audioCall.statusHints.reconnecting';
    }
    return 'call.callModal.connecting';
  }

  ngOnDestroy(): void {
    this.sb.unsubscribeAll();
    this.clearRemoteVideoFrameStallTimeout();
    this.clearRemoteVideoFramePollTimeout();
    this.attachRemoteVideoStream(null);
  }

  ngAfterViewInit(): void {
    this.sb.sub = combineLatest([
      this.activeAudioCallUiService.minimized$,
      this.connectAudioService.remoteStream$,
      this.connectAudioService.remoteVideoActive$,
    ]).subscribe((value: [boolean, MediaStream | null, boolean]): void => {
      const isMinimized: boolean = value[0];
      const stream: MediaStream | null = value[1];
      const remoteVideoActive: boolean = value[2];

      if (!isMinimized) {
        this.remoteVideoTrackActive = false;
        this.attachRemoteVideoStream(null);
        return;
      }

      this.remoteVideoTrackActive = remoteVideoActive;
      this.attachPlaybackElement();
      this.attachRemoteVideoStream(remoteVideoActive ? stream : null);
    });
  }

  protected async openFullCall(): Promise<void> {
    await this.audioCallService.openCurrentAudioCallScreen();
  }

  protected async openFullCallFromPointer(): Promise<void> {
    if (this.suppressNextPointerOpen) {
      this.suppressNextPointerOpen = false;
      return;
    }

    await this.openFullCall();
  }

  protected resetPointerOpenSuppression(): void {
    this.suppressNextPointerOpen = false;
  }

  protected onWidgetDragEnded(event: CdkDragEnd): void {
    this.suppressNextPointerOpen = event.distance.x !== 0 || event.distance.y !== 0;
  }

  private get hasActiveCall(): boolean {
    return this.audioCallService.hasStartedActiveAudioCall();
  }

  private attachPlaybackElement(): void {
    if (!this.activeAudioCallUiService.isMinimized) return;

    const audioOutput: HTMLAudioElement | null = this.audioOutputElement;
    if (!audioOutput) return;

    this.connectAudioService.webrtcAudioService.attachPlaybackElement(audioOutput);
  }

  private attachRemoteVideoStream(stream: MediaStream | null): void {
    this.remoteStream = stream;
    this.syncRemoteVideoElement();
    this.syncRemoteVideoPresence();
  }

  private syncRemoteVideoElement(): void {
    if (!this.remoteVideoElement) return;
    if (this.remoteVideoElement.srcObject !== this.remoteStream) {
      this.remoteVideoElement.srcObject = this.remoteStream;
    }
    if (!this.remoteStream) {
      this.remoteVideoElement.pause();
      return;
    }

    this.remoteVideoElement.play().catch((): void => {
      // Ignore autoplay errors for minimized preview video
    });
  }

  private hasActiveRemoteVideo(stream: MediaStream | null): boolean {
    if (!this.remoteVideoTrackActive) return false;
    if (!stream) return false;
    return stream.getVideoTracks().some((track: MediaStreamTrack): boolean => {
      if (track.readyState !== 'live') return false;
      return track.muted !== true;
    });
  }

  private hasLiveRemoteVideo(stream: MediaStream | null): boolean {
    if (!stream) return false;
    return stream.getVideoTracks().some((track: MediaStreamTrack): boolean => track.readyState === 'live');
  }

  private syncRemoteVideoPresence(): void {
    const remoteVideoElement: HTMLVideoElement | null = this.remoteVideoElement;
    if (!remoteVideoElement || !this.remoteVideoTrackActive || !this.hasLiveRemoteVideo(this.remoteStream)) {
      this.clearRemoteVideoPresence();
      return;
    }

    if (!this.hasActiveRemoteVideo(this.remoteStream)) {
      this.setRemoteVideoPresence(false);
    }

    this.startRemoteVideoPresenceProbe(remoteVideoElement);
  }

  private clearRemoteVideoPresence(): void {
    this.remoteVideoProbeVersion += 1;
    this.clearRemoteVideoFrameStallTimeout();
    this.clearRemoteVideoFramePollTimeout();
    this.setRemoteVideoPresence(false);
  }

  private startRemoteVideoPresenceProbe(video: HTMLVideoElement): void {
    const probeVersion: number = this.remoteVideoProbeVersion + 1;
    this.remoteVideoProbeVersion = probeVersion;
    this.clearRemoteVideoFramePollTimeout();
    this.setRemoteVideoPresence(false);

    if (video.videoWidth > 0 && video.videoHeight > 0 && !video.paused) {
      this.setRemoteVideoPresence(true);
      this.scheduleRemoteVideoFrameStallTimeout(probeVersion);
    }

    if (typeof video.requestVideoFrameCallback !== 'function') {
      this.startRemoteVideoFramePoll(video, probeVersion);
      return;
    }

    let lastPresentedFrames: number | null = null;
    let lastMediaTime: number | null = null;
    const onFrame: VideoFrameRequestCallback = (_now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata): void => {
      if (probeVersion !== this.remoteVideoProbeVersion) return;
      if (!this.hasActiveRemoteVideo(this.remoteStream)) {
        this.clearRemoteVideoPresence();
        return;
      }

      const isFreshFrame: boolean = this.isFreshVideoFrame(metadata, lastPresentedFrames, lastMediaTime);
      lastPresentedFrames = metadata.presentedFrames;
      lastMediaTime = metadata.mediaTime;

      if (isFreshFrame) {
        this.setRemoteVideoPresence(true);
        this.scheduleRemoteVideoFrameStallTimeout(probeVersion);
      }

      video.requestVideoFrameCallback(onFrame);
    };

    video.requestVideoFrameCallback(onFrame);
  }

  private startRemoteVideoFramePoll(video: HTMLVideoElement, probeVersion: number): void {
    let lastFrameCount: number | null = this.getVideoFrameCount(video);
    let lastFreshFrameAt: number = Date.now();

    const poll = (): void => {
      if (probeVersion !== this.remoteVideoProbeVersion) return;
      if (!this.hasActiveRemoteVideo(this.remoteStream)) {
        this.clearRemoteVideoPresence();
        return;
      }

      const currentFrameCount: number | null = this.getVideoFrameCount(video);
      const hasFrame: boolean = video.videoWidth > 0 && video.videoHeight > 0 && !video.paused;
      const now: number = Date.now();

      if (currentFrameCount === null || lastFrameCount === null) {
        this.setRemoteVideoPresence(hasFrame);
        this.scheduleRemoteVideoFramePoll(probeVersion, poll);
        return;
      }

      if (currentFrameCount > lastFrameCount) {
        lastFrameCount = currentFrameCount;
        lastFreshFrameAt = now;
        this.setRemoteVideoPresence(true);
      } else if (now - lastFreshFrameAt >= this.remoteVideoFrameStallMs) {
        this.setRemoteVideoPresence(false);
      }

      this.scheduleRemoteVideoFramePoll(probeVersion, poll);
    };

    this.scheduleRemoteVideoFramePoll(probeVersion, poll);
  }

  private scheduleRemoteVideoFramePoll(
    probeVersion: number,
    poll: () => void,
  ): void {
    this.clearRemoteVideoFramePollTimeout();
    this.remoteVideoFramePollTimeout = setTimeout((): void => {
      if (probeVersion !== this.remoteVideoProbeVersion) return;
      poll();
    }, this.remoteVideoFramePollIntervalMs);
  }

  private isFreshVideoFrame(
    metadata: VideoFrameCallbackMetadata,
    lastPresentedFrames: number | null,
    lastMediaTime: number | null,
  ): boolean {
    if (lastPresentedFrames === null || lastMediaTime === null) return true;
    return metadata.presentedFrames !== lastPresentedFrames || metadata.mediaTime !== lastMediaTime;
  }

  private scheduleRemoteVideoFrameStallTimeout(probeVersion: number): void {
    this.clearRemoteVideoFrameStallTimeout();

    this.remoteVideoFrameStallTimeout = setTimeout((): void => {
      this.remoteVideoFrameStallTimeout = null;
      if (probeVersion !== this.remoteVideoProbeVersion) return;

      this.setRemoteVideoPresence(false);
    }, this.remoteVideoFrameStallMs);
  }

  private clearRemoteVideoFrameStallTimeout(): void {
    if (!this.remoteVideoFrameStallTimeout) return;

    clearTimeout(this.remoteVideoFrameStallTimeout);
    this.remoteVideoFrameStallTimeout = null;
  }

  private clearRemoteVideoFramePollTimeout(): void {
    if (!this.remoteVideoFramePollTimeout) return;

    clearTimeout(this.remoteVideoFramePollTimeout);
    this.remoteVideoFramePollTimeout = null;
  }

  private getVideoFrameCount(video: VideoElementFrameMetrics): number | null {
    if (typeof video.getVideoPlaybackQuality === 'function') {
      const quality: VideoPlaybackQuality = video.getVideoPlaybackQuality();
      if (Number.isFinite(quality.totalVideoFrames)) return quality.totalVideoFrames;
    }

    if (typeof video.webkitDecodedFrameCount === 'number' && Number.isFinite(video.webkitDecodedFrameCount)) {
      return video.webkitDecodedFrameCount;
    }

    if (typeof video.mozPaintedFrames === 'number' && Number.isFinite(video.mozPaintedFrames)) {
      return video.mozPaintedFrames;
    }

    return null;
  }

  private setRemoteVideoPresence(isPresent: boolean): void {
    if (this.hasRemoteVideo === isPresent) return;

    this.hasRemoteVideo = isPresent;
    this.detectChangesSafely();
  }

  private detectChangesSafely(): void {
    if (this.cdr instanceof ViewRef && this.cdr.destroyed) return;
    this.cdr.detectChanges();
  }
}
